// Answers of the knowledge routes (platform p1e at f3a2d0f: knowledge/views.py), camelCase on the
// wire; the contract has no response schemas for them (ADR 0015, as ADR 0003 for the registry).
import { z } from "zod";

export const IngestionStatus = z.enum(["queued", "running", "ready", "failed"]);
export type IngestionStatus = z.infer<typeof IngestionStatus>;

/** One document's ingestion in one generation. */
export const IngestionView = z.object({
  generationId: z.string(),
  digest: z.string(),
  status: IngestionStatus,
  attempts: z.number().int(),
  pages: z.number().int().nullable(),
  chunks: z.number().int().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  tokens: z.number().int().nullable(),
  /** Decimal text; the platform gives no currency for knowledge costs. */
  cost: z.string().nullable(),
});
export type IngestionView = z.infer<typeof IngestionView>;

/** A knowledge base's document (named so beside the registry's DocumentView). */
export const KbDocument = z.object({
  id: z.string(),
  name: z.string(),
  sourceType: z.enum(["upload", "url"]),
  mediaType: z.string().nullable(),
  sizeBytes: z.number().int(),
  sha256: z.string(),
  status: z.enum(["active", "deleting"]),
  createdAt: z.string(),
  ingestions: z.array(IngestionView),
});
export type KbDocument = z.infer<typeof KbDocument>;

export const GenerationStatus = z.enum(["building", "live", "retired", "failed"]);
export type GenerationStatus = z.infer<typeof GenerationStatus>;

/** An index generation of a knowledge base, with its counts and the environments it serves. */
export const GenerationView = z.object({
  id: z.string(),
  digest: z.string(),
  status: GenerationStatus,
  environments: z.array(z.string()),
  ready: z.number().int(),
  failed: z.number().int(),
  waiting: z.number().int(),
  cost: z.string().nullable(),
  createdAt: z.string(),
  liveAt: z.string().nullable(),
});
export type GenerationView = z.infer<typeof GenerationView>;

/** An upload's answer; `unchanged` when the knowledge base already holds the bytes. */
export const Uploaded = z.object({
  documentId: z.string(),
  name: z.string(),
  status: z.enum(["queued", "unchanged"]),
});
export type Uploaded = z.infer<typeof Uploaded>;

export const Deleting = z.object({ document: z.string(), status: z.literal("deleting") });
export const Queued = z.object({ queued: z.number().int() });
export const Refreshing = z.object({ status: z.literal("refreshing") });

/** The largest upload the platform takes, whatever the spec says (its FIFTY_MIB). */
export const MAX_DOCUMENT_BYTES = 52_428_800;

/** The ingestion of `document` in the generation `env` serves, if any. */
export function ingestionIn(
  document: KbDocument,
  generations: readonly GenerationView[],
  env: string,
): IngestionView | undefined {
  const served = generations.find((generation) => generation.environments.includes(env));
  return served && document.ingestions.find((row) => row.generationId === served.id);
}
